// @typescript-eslint/no-explicit-any
"use client"

import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, EffectFade, Pagination } from "swiper/modules"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import "swiper/css/effect-fade"
import { useEffect, useRef, useState } from "react"
import { SingleCarouselCardProps } from "../util/enum"
import { MdNavigateNext } from "react-icons/md"
import { GrFormPrevious } from "react-icons/gr"
import { getBreakpoints } from "../util/SwiperCard"

export default function SingleSwiper({
    pageName,
    carousel_title,
    crouselData,
    renderItem,
    showPagination = false,
    slideDelayTime = 4000,
    slideClassName,
    onSwiperReady,
    autoHeight = true,
    loop = true,
}: SingleCarouselCardProps) {

    const [isBeginning, setIsBeginning] = useState(true)
    const [hasEnoughContent, setHasEnoughContent] = useState(false)
    const swiperRef = useRef<SwiperType | null>(null)

    // Function to calculate if navigation should be shown based on viewport and content
    const calculateHasEnoughContent = (swiper: SwiperType) => {
        const totalSlides = swiper.slides.length
        const slidesPerView = swiper.params.slidesPerView
        return totalSlides > (typeof slidesPerView === "number" ? slidesPerView : 1)
    }

    // Recalculate when crouselData changes (fixes pagination not showing when data loads async)
    useEffect(() => {
        if (swiperRef.current) {
            setHasEnoughContent(calculateHasEnoughContent(swiperRef.current))
        }
    }, [crouselData])

    const handlePrev = () => {
        if (!swiperRef.current) return;
        if (isBeginning) {
            swiperRef.current.slideTo(swiperRef.current.slides.length - 1);
        } else {
            swiperRef.current.slidePrev();
        }
    };

    const handleNext = () => {
        if (!swiperRef.current) return;
        swiperRef.current.slideNext();
    };


    return (
        <div className={`relative z-8!`}>

            <div className="absolute inset-0 flex items-center justify-between pointer-events-none px-0.5 md:px-2 lg:px-4">
                {carousel_title ? (
                    <h3 className="crousel_head_text absolute top-5 left-1/2 -translate-x-1/2">
                        {carousel_title}
                    </h3>
                ) : null}

                {(hasEnoughContent && !showPagination) ? (
                    <>
                        <button
                            type="button"
                            onClick={handlePrev}
                            aria-label="Previous"
                            className="hidden md:flex items-center justify-center btn_global icon_button_prev rounded-full border border-line-strong pointer-events-auto"
                        >
                            <GrFormPrevious className="text-blackishBrown text-xl" />
                        </button>
                        <button
                            type="button"
                            onClick={handleNext}
                            aria-label="Next"
                            className="hidden md:flex items-center justify-center btn_global icon_button_forward rounded-full border border-line-strong pointer-events-auto"
                        >
                            <MdNavigateNext className="text-blackishBrown text-xl" />
                        </button>
                    </>
                ) : null}
            </div>
            <div className={`swiper_slider_container overflow-hidden`}>
                <Swiper
                    autoHeight={autoHeight}
                    pagination={(showPagination && hasEnoughContent) ? { clickable: true, dynamicBullets: true } : false}
                    modules={[Pagination, Autoplay, EffectFade]}
                    grabCursor
                    loop={loop}
                    autoplay={{
                        delay: slideDelayTime,
                        disableOnInteraction: true,
                        pauseOnMouseEnter: true,
                    }}
                    slidesPerView={1}
                    spaceBetween={24}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper
                        setIsBeginning(swiper.isBeginning)
                        setHasEnoughContent(calculateHasEnoughContent(swiper))
                        onSwiperReady?.()
                    }}
                    onSlideChange={(swiper) => {
                        setIsBeginning(swiper.isBeginning)
                    }}
                    onBreakpoint={(swiper) => {
                        setHasEnoughContent(calculateHasEnoughContent(swiper))
                    }}
                    breakpoints={getBreakpoints(pageName)}
                    className={`my-swiper ${showPagination ? "swiper-show-pagination" : ""} ${autoHeight ? "" : "[&_.swiper-wrapper]:items-stretch"}`.trim()}
                >
                    {crouselData?.map((item: any, index: number) => (
                        <SwiperSlide key={index} className={`px-0! ${showPagination ? "pb-6 md:pb-8" : ""} ${slideClassName ?? ""}`.trim()}>
                            {renderItem(item, index)}
                        </SwiperSlide>
                    ))}
                </Swiper>

            </div>
        </div>
    )
}

